import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { FormControl, Select, MenuItem, Button, Box } from '@mui/material';
import { SiteWheelPrize } from 'components/api/siteWheel';
import styles from './WheelPrizeSettings.module.scss';

interface Props {
  prize: SiteWheelPrize;
  onUpdate: (id: number, payload: Partial<SiteWheelPrize>) => void;
  onDelete: (id: number) => void;
  onOpenGameSelect: (id: number) => void;
}

const SortablePrizeRow: React.FC<Props> = ({ prize, onUpdate, onDelete, onOpenGameSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: prize.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTypeChange = (e: any) => {
    const newType = e.target.value;
    // Auto-update label based on new type if needed
    let newLabel = prize.label;
    if (newType === 'cash') newLabel = `$${prize.amount || 0}`;
    if (newType === 'deposit_match') newLabel = `x${prize.depositMatchPercent || 0}%`;
    if (newType === 'free_spin') newLabel = `${prize.freeSpinCount || 0} Free Spins`;

    onUpdate(prize.id, { prizeType: newType, label: newLabel, probability: '0' });
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = e.target.value;
    onUpdate(prize.id, { amount, label: `$${amount}` });
  };

  const handleDepositPercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percent = e.target.value;
    onUpdate(prize.id, { depositMatchPercent: percent, label: `x${percent}%` });
  };

  const handleFreeSpinCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const count = parseInt(e.target.value) || 0;
    onUpdate(prize.id, { freeSpinCount: count, label: `${count} Free Spins` });
  };

  const handleProbabilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(prize.id, { probability: e.target.value });
  };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? styles.dragging : ''}>
      <td>
        <div className={styles.dragHandle} {...attributes} {...listeners}>
          <GripVertical size={20} />
        </div>
      </td>
      <td>
        <FormControl size="small" className={styles.selectContainer}>
          <Select value={prize.prizeType} onChange={handleTypeChange}>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="deposit_match">Bonus</MenuItem>
            <MenuItem value="free_spin">Spin</MenuItem>
          </Select>
        </FormControl>
      </td>
      <td>
        <div className={styles.configCell}>
          {prize.prizeType === 'cash' && (
            <input
              placeholder="Amount"
              className={styles.inputField}
              value={prize.amount}
              onChange={handleAmountChange}
            />
          )}
          {prize.prizeType === 'deposit_match' && (
            <input
              placeholder="Percent (%)"
              className={styles.inputField}
              value={prize.depositMatchPercent}
              onChange={handleDepositPercentChange}
            />
          )}
          {prize.prizeType === 'free_spin' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <input
                placeholder="Count"
                className={styles.inputField}
                value={prize.freeSpinCount}
                onChange={handleFreeSpinCountChange}
              />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#2a8039',
                  '&:hover': { backgroundColor: '#1e5e2a' },
                  fontSize: '10px',
                  padding: '1px 6px',
                  minWidth: 'auto',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => onOpenGameSelect(prize.id)}
              >
                Select game
              </Button>
            </Box>
          )}
        </div>
      </td>
      <td>
        <input
          placeholder="Probability"
          className={styles.inputField}
          value={prize.probability}
          onChange={handleProbabilityChange}
        />
      </td>
      <td>
        <div className={styles.labelDisplay}>{prize.label}</div>
      </td>
    </tr>
  );
};

export default SortablePrizeRow;
